import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Loader2, Sparkles, Check, X, AlertCircle } from "lucide-react";

export interface ExtractedData {
  institutionName?: string;
  accountNumber?: string;
  accountType?: string;
  accountHolder?: string;
  balance?: string;
  date?: string;
  username?: string;
  notes?: string;
  additionalInfo?: Record<string, string>;
  confidence: number;
  rawDescription: string;
}

interface AIExtractionReviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentPreviewUrl?: string;
  extractedData: ExtractedData | null;
  isAnalyzing?: boolean;
  onApprove: (data: ExtractedData) => void;
  onSkip: () => void;
  analysisType?: "financial" | "digital_asset";
}

export function AIExtractionReviewModal({
  open,
  onOpenChange,
  documentPreviewUrl,
  extractedData,
  isAnalyzing = false,
  onApprove,
  onSkip,
  analysisType = "financial",
}: AIExtractionReviewModalProps) {
  const [editedData, setEditedData] = useState<ExtractedData | null>(null);

  useEffect(() => {
    if (extractedData) {
      setEditedData({ ...extractedData });
    }
  }, [extractedData]);

  const handleFieldChange = (field: keyof ExtractedData, value: string) => {
    if (editedData) {
      setEditedData({ ...editedData, [field]: value });
    }
  };

  const handleApprove = () => {
    if (editedData) {
      onApprove(editedData);
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return "text-green-600 dark:text-green-400";
    if (confidence >= 0.5) return "text-yellow-600 dark:text-yellow-400";
    return "text-red-600 dark:text-red-400";
  };

  const getConfidenceLabel = (confidence: number) => {
    if (confidence >= 0.8) return "High confidence";
    if (confidence >= 0.5) return "Medium confidence";
    return "Low confidence";
  };

  const financialFields = [
    { key: "institutionName", label: "Institution Name", placeholder: "e.g., Bank of America" },
    { key: "accountType", label: "Account Type", placeholder: "e.g., Checking, Savings, IRA" },
    { key: "accountNumber", label: "Account Number", placeholder: "e.g., ****1234" },
    { key: "accountHolder", label: "Account Holder", placeholder: "Account holder name" },
    { key: "balance", label: "Balance", placeholder: "Current balance" },
    { key: "date", label: "Statement Date", placeholder: "As of date" },
  ];

  const digitalAssetFields = [
    { key: "institutionName", label: "Service/Company", placeholder: "e.g., Gmail, Netflix" },
    { key: "accountType", label: "Account Type", placeholder: "e.g., Email, Streaming" },
    { key: "username", label: "Username/Email", placeholder: "Login identifier" },
    { key: "notes", label: "Notes", placeholder: "Additional information" },
  ];

  const fields = analysisType === "financial" ? financialFields : digitalAssetFields;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            AI Document Analysis
          </DialogTitle>
          <DialogDescription>
            {isAnalyzing
              ? "Analyzing your document..."
              : "Review the extracted information below. You can edit any fields before saving."}
          </DialogDescription>
        </DialogHeader>

        {isAnalyzing ? (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-muted-foreground">Extracting information from your document...</p>
          </div>
        ) : editedData ? (
          <div className="space-y-6">
            {documentPreviewUrl && (
              <div className="border rounded-lg overflow-hidden bg-muted/30">
                <img
                  src={documentPreviewUrl}
                  alt="Document preview"
                  className="max-h-48 w-full object-contain"
                  data-testid="img-document-preview"
                />
              </div>
            )}

            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {editedData.rawDescription}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-sm font-medium ${getConfidenceColor(editedData.confidence)}`}>
                  {getConfidenceLabel(editedData.confidence)}
                </span>
                <Progress 
                  value={editedData.confidence * 100} 
                  className="w-20 h-2"
                  data-testid="progress-confidence"
                />
              </div>
            </div>

            <div className="grid gap-4">
              {fields.map((field) => {
                const value = editedData[field.key as keyof ExtractedData];
                if (typeof value === "object") return null;
                
                return (
                  <div key={field.key} className="grid gap-2">
                    <Label htmlFor={field.key}>{field.label}</Label>
                    <Input
                      id={field.key}
                      value={(value as string) || ""}
                      onChange={(e) =>
                        handleFieldChange(field.key as keyof ExtractedData, e.target.value)
                      }
                      placeholder={field.placeholder}
                      data-testid={`input-extraction-${field.key}`}
                    />
                  </div>
                );
              })}

              {editedData.additionalInfo && Object.keys(editedData.additionalInfo).length > 0 && (
                <div className="grid gap-2">
                  <Label>Additional Information</Label>
                  <div className="p-3 bg-muted/30 rounded-lg space-y-1">
                    {Object.entries(editedData.additionalInfo).map(([key, value]) => (
                      <div key={key} className="flex items-center gap-2 text-sm">
                        <Badge variant="outline">{key}</Badge>
                        <span>{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={onSkip}
            disabled={isAnalyzing}
            data-testid="button-skip-extraction"
          >
            <X className="h-4 w-4 mr-2" />
            Skip
          </Button>
          <Button
            onClick={handleApprove}
            disabled={isAnalyzing || !editedData}
            data-testid="button-approve-extraction"
          >
            <Check className="h-4 w-4 mr-2" />
            Save Extracted Info
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
